import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Grid, IconButton, LinearProgress, Link, styled, SvgIcon, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { apiVideoLibraryProvider, VideoManagerState } from '../../store/videoManagerSlice';
import { OrganisationContext } from '../../App';
import { RootState } from '../../index';
import SimpleVideoThumbnail from '../common/SimpleVideoThumbnail';
import PaginationTable from '../Pagination';
import ClipVideoMenu from './ClipVideoMenu';
import VideoMenu from './VideoMenu';
import { EditVideoRequest } from '../modal/VideoEditRequest';
import { ReUploadVideo } from '../modal/VideoReUpload';
import { ChildVideo, VideoManagerItem, VideoManagerProps } from '../types/videos';
import { getApiBaseUrl } from '../../utils/env';
import { RHVideoPreview } from '../modal/RHVideoPreview';
import { VIDEO_STATUS } from '../types/videoStatus';
import { VideoClip } from '../modal/VideoClip';
import { ReactComponent as CopyContentIcon } from '../../icons/copy-content.svg';

import Moment from 'react-moment';
import copyToClipboard from '../utils/copyToClipboard';
import { VideoItemSkeleton } from './VideoItemSkeleton';
import { VideoFxRow } from './VideoFxRow';
import { ChildVideoItemSkeleton } from './ChildVideoItemSkeleton';
import { VideoCard } from './thumbnail/VideoCard';
import { VideoTranscript } from '../types/videoTranscript';
import { EditVideoDialog } from '../modal/EditVideoDialog';

const ApprovalCheckbox = styled(CheckCircleIcon)`
    fill: red;
    position: absolute;
    right: -6px;
    top: -6px;
    background-color: white;
    border-radius: 50%;
`;

const GridRoot = styled(Grid)`
    position: relative;

    &:after {
        content: ' ';
        position: absolute;
        width: 100%;
        height: 1px;
        background-color: rgba(224, 224, 224, 1);
        bottom: 0;
        left: 0;
    }
`;

export const VideoTitle = styled(Typography)`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    margin: 10px 0 6px 0;
    max-width: 250px;
    word-break: break-word;
    padding-right: 10px;
    cursor: pointer;

    &::after {
        content: '';
        display: inline-block;
        width: 14px;
        height: 14px;
        margin-left: 7px;
        margin-top: 2px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        vertical-align: top;
    }

`;

const VideoColumnTitle = styled(Typography)`
    font-weight: bold;
`;

const VideoClipPlaceholder = {
  status: VIDEO_STATUS.PREPARING,
  thumbnail: '',
  title: '',
  title_translated: '',
  duration: 0,
  score: 0,
  approved: false,
  uuid: '',
  format: 'vertical',
  media_id: '',
  domain: null,
  clip_id: null,
  project_id: null,
  summary: null,
  preview_uri: null,
  publish_date: null,
  created_at: null,
  has_been_played: null,
} as unknown as ChildVideo;

export default function VideoTabContent() {
  const refreshButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();
  const {
    selectedOrganisation,
    selectedOrganisationObject,
    organisations
  } = useContext(OrganisationContext);
  const org = organisations.find(({ domain }) => domain === selectedOrganisation);
  const clipConversionEnabled = org?.clip_conversion_enabled;
  const {
    pagination, videos, busy, searchText, videosStale
  } = useSelector<RootState, VideoManagerState>((state) => state.videoManager);
  const [editVideoRequest, setEditVideoRequest] = useState<string>();
  const [editVideoTitle, setEditVideoTitle] = useState<{ title: string, uuid: string }>();
  const [reUploadVideoRequest, setReUploadVideoRequest] = useState<VideoManagerItem | ChildVideo>();
  const [previewVideo, setPreviewVideo] = useState<VideoManagerProps>();
  const [clipVideo, setClipVideo] = useState<{ video: VideoManagerItem, format: string}>();
  const [metadataGenerationInProgress, setMetadataGenerationInProgress] = useState(false);
  const [convertToVerticalInProgress, setConvertToVerticalInProgress] = useState(false);

  const updateList = () => {
    dispatch(apiVideoLibraryProvider({
      publisher: selectedOrganisation,
      limit: pagination.itemsPerPage,
      page: pagination.currentPage,
      search: searchText,
      toAction: false,
    }));
  };

  useEffect(() => {
    if (!selectedOrganisation) return;

    if (!selectedOrganisationObject?.is_rightsholder) {
      updateList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.itemsPerPage, pagination.currentPage, selectedOrganisation, searchText]);

  useEffect(() => {
    if (previewVideo) {
      let transcripts: VideoTranscript[] = [];

      for (const video of videos) {
        if (video.uuid === previewVideo.uuid) {
          transcripts = video.transcripts;
          break;
        }
        const childVideo = video.child_videos.find(({ uuid }) => uuid === previewVideo.uuid);
        if (childVideo) {
          transcripts = childVideo.transcripts;
          break;
        }
      }

      if (transcripts.length > 0) {
        setPreviewVideo({
          ...previewVideo,
          transcripts,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  useEffect(() => {
    if (videosStale) {
      updateList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videosStale]);

  const download = async (uuid: string, rightsholder: string) => {
    const { data } = await axios.get(`${getApiBaseUrl()}/video-clip/${rightsholder}/download-clip`, {
      params: {
        uuid
      }
    });
    window.location.href = data;
  };

  const convertToVertical = async (uuid: string) => {
    if (convertToVerticalInProgress) {
      alert('Conversion is already in progress. Please wait until it is completed.');
      return;
    }

    setConvertToVerticalInProgress(true);
    await axios.post(`${getApiBaseUrl()}/video-clip/${selectedOrganisation}/convert-to-vertical`, {
      uuid
    }).then(() => {
      alert('Conversion has been requested and will be ready soon. Please wait.');
      setConvertToVerticalInProgress(false);
    }).catch(e => {
      setConvertToVerticalInProgress(false);
      console.log('>>> error:', e);
      alert(typeof e === 'string' ?  e : 'An error occurred while converting to vertical. Please' +
        ' try again' +
        ' later.');
    })
  }

  const generateMetadata = async (uuid: string) => {
    if (metadataGenerationInProgress) {
      alert('Metadata generation is already in progress. Please wait until it is completed.');
      return;
    }

    setMetadataGenerationInProgress(true);
    await axios.post(`${getApiBaseUrl()}/video-clip/${selectedOrganisation}/generate-metadata`, {
      uuid
    }).then(() => {
      alert('Metadata generation has been requested and will be ready soon. Please wait.');
      setMetadataGenerationInProgress(false);
    }).catch(e => {
      setMetadataGenerationInProgress(false);
      console.log('>>> error:', e);
      alert(typeof e === 'string' ?  e : 'An error occurred while generating metadata. Please' +
        ' try again' +
        ' later.');
    });
  }

  const onEditTitle = async (newTitle: string) => {
    if (!editVideoTitle?.uuid) return;

    await axios.patch(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/${editVideoTitle.uuid}`, {
      title: newTitle
    });
    setEditVideoTitle(undefined);
  };

  const deleteSource = async (video: VideoManagerItem) => {
    await axios.delete(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/source/${video.uuid}`);
    updateList();
  };

  const deleteClip = async (video: ChildVideo) => {
    await axios.delete(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/clip/${video.uuid}`);
    updateList();
  };

  const getPremierProjectUrl = async (projectId: string, clipId: string) => {
    const { data: { url } } = await axios.get(`${getApiBaseUrl()}/video-clip/${selectedOrganisation}/premier-project-link`, {
      params: {
        projectId,
        clipId
      }
    });
    window.location.href = url;
  };

  const approveVideo = async (uuid: string, status: VIDEO_STATUS) => {
    await axios.post(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/manage/approval`, {
      uuid, status
    }).catch(e => {
      console.log('>>> error:', e);
    });
    updateList();
  };

  return <>
    {clipVideo &&
      <VideoClip title={clipConversionEnabled ? 'Re-clip' : 'Clip'} open={Boolean(clipVideo)}
                 handleClose={() => setClipVideo(undefined)} format={clipVideo.format}
                 video={clipVideo.video}/>}
    {previewVideo &&
      <RHVideoPreview open={Boolean(previewVideo)} handleClose={() => {
        setPreviewVideo(undefined);
        updateList();
      }}
                         video={previewVideo}/>}
    {editVideoRequest && <EditVideoRequest open={Boolean(editVideoRequest)}
                                           handleClose={() => setEditVideoRequest(undefined)}
                                           video={editVideoRequest}/>}
    {reUploadVideoRequest && <ReUploadVideo open={Boolean(reUploadVideoRequest)}
                                            handleClose={() => setReUploadVideoRequest(undefined)}
                                            video={reUploadVideoRequest}/>}

    { editVideoTitle && <EditVideoDialog
      open={Boolean(editVideoTitle.uuid)}
      videoTitle={editVideoTitle.title}
      handleClose={() => setEditVideoTitle(undefined)}
      onSave={onEditTitle}
    />}
    <Box sx={{ m: 2, pb: 2 }}>
      <GridRoot container>
        <Grid item xs={12}>
          <Grid container justifyContent={'space-between'}>
            <Grid item xs={3}>
              <VideoColumnTitle>Source</VideoColumnTitle>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                ref={refreshButtonRef}
                onClick={() => {
                  updateList();
                  refreshButtonRef.current?.classList.add('animate');
                  setTimeout(() => {
                    refreshButtonRef.current?.classList.remove('animate');
                  }, 500);
                }}
                size="small"
                sx={{
                  color: 'primary.main',
                  ml: 'auto',
                  '&.animate': {
                    animation: 'spin 0.5s ease-in-out'
                  },
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  }
                }}
              >
                <RefreshIcon/>
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        {videos.length > 0 && videos.map((video, index: number) => {
          if ([VIDEO_STATUS.PREPARING, VIDEO_STATUS.ERRORED].includes(video.status)) {
            return <VideoFxRow uuid={video.uuid} in={!video.removed} key={video.uuid}>
              <VideoItemSkeleton video={video}/>
            </VideoFxRow>;
          }
          return (
            <VideoFxRow uuid={video.uuid} in={!video.removed} key={video.uuid}>
              <Grid container wrap="nowrap" spacing={2}>
                <Grid item xs={3} sx={{ minWidth: '300px' }}>
                <VideoCard
                    video={video}
                    onClick={() => setPreviewVideo({
                      parent_media_id: '',
                      parent_title: video.title,
                      parent_title_translated: '',
                      has_variant: false,
                      can_manage_video: false,
                      summary: video.summary,
                      media_id: video.media_id,
                      preview_uri: video.preview_uri,
                      rightsholder: video.domain,
                      video_type: 'pixels',
                      format: video.format,
                      title: video.title,
                      publish_date: new Date(video.publish_date),
                      transcripts: video.transcripts,
                      uuid: video.uuid,
                      disable_custom_caption: video.disable_custom_caption,
                      opus_title: video.opus_title,
                      opus_description: video.opus_description,
                      opus_clip_genre: video.opus_clip_genre,
                      opus_hashtags: video.opus_hashtags,
                      // asa_compliant only available with opus clip (child video)
                      asa_compliant: false,
                      creator_name: video.creator_name,
                      creator_url: video.creator_channel_url,
                      creator_logo_url: video.creator_logo_url,
                      use_org_meta: video.use_org_meta,
                    })}
                    menu={<VideoMenu video={video}
                                     onDownloadMp4={() => download(video.uuid, video.domain)}
                                     onDelete={async () => deleteSource(video)}
                                     onClip={(format: string) => setClipVideo({ video, format })}
                                     onGenerateMetadata={() => generateMetadata(video.uuid)}
                                     reClip={Boolean(video.child_videos.length > 0)}
                                     onReUpload={() => {
                                       setReUploadVideoRequest(video);
                                     }}
                                     onConvertToVertical={() => convertToVertical(video.uuid)}
                                     onKeepVideo={() => console.warn('unimplemented on keep video')}
                                     onApprove={() => approveVideo(video.uuid, video.status === VIDEO_STATUS.APPROVED ? VIDEO_STATUS.UNAPPROVED : VIDEO_STATUS.APPROVED)}/>}
                  />
                  <VideoTitle onClick={() => setEditVideoTitle({
                    title: video.title,
                    uuid: video.uuid,
                  })}>{video.title}</VideoTitle>
                  {video.approved_at && <Typography fontSize={13}>Approved at: <Moment
                        format="D MMM YYYY">{video.approved_at}</Moment></Typography>}
                      <Typography fontSize={13}>Published date: <Moment
                        format="D MMM YYYY">{video.publish_date}</Moment></Typography>
                      <Typography fontSize={13}>Ingested at: <Moment
                        format="D MMM YYYY">{video.created_at}</Moment></Typography>
                      {video.media_id && <div title={`Copy media id to clipboard`}
                                              style={{ cursor: 'pointer', marginTop: '10px' }}>
                        <SvgIcon
                          onClick={copyToClipboard(video.media_id)}><CopyContentIcon/></SvgIcon>
                      </div>}
                </Grid>
                <Grid item xs={9}>
                  <Grid container spacing={1}>
                    {video.clip_request_in_progress &&
                      <ChildVideoItemSkeleton video={VideoClipPlaceholder}/>}
                    {video.child_videos.map((childVideo) => {
                      if (childVideo.status === VIDEO_STATUS.PREPARING || childVideo.status === VIDEO_STATUS.ERRORED) {
                        return (<ChildVideoItemSkeleton key={childVideo.uuid} video={childVideo}/>);
                      }
                      return (
                        <Grid data-uuid={childVideo.uuid} item key={childVideo.uuid} lg={3} xs={4}>
                          <Grid container>
                            <Grid item>
                              <Box sx={{ position: 'relative' }}>
                                <Link data-uuid={childVideo.uuid} data-status={childVideo.status}
                                      title={childVideo.preview_uri}
                                      onClick={() => setPreviewVideo({
                                        parent_media_id: '',
                                        parent_title: childVideo.title,
                                        parent_title_translated: '',
                                        has_variant: false,
                                        can_manage_video: false,
                                        summary: childVideo.summary,
                                        media_id: childVideo.media_id,
                                        preview_uri: childVideo.preview_uri,
                                        rightsholder: childVideo.domain,
                                        video_type: 'pixels',
                                        format: childVideo.format,
                                        title: childVideo.title,
                                        publish_date: new Date(childVideo.publish_date),
                                        transcripts: childVideo.transcripts,
                                        uuid: childVideo.uuid,
                                        disable_custom_caption: childVideo.disable_custom_caption,
                                        opus_title: childVideo.opus_title,
                                        opus_description: childVideo.opus_description,
                                        opus_clip_genre: childVideo.opus_clip_genre,
                                        opus_hashtags: childVideo.opus_hashtags,
                                        asa_compliant: childVideo.asa_compliant,
                                        creator_name: childVideo.creator_name,
                                        creator_url: childVideo.creator_channel_url,
                                        creator_logo_url: childVideo.creator_logo_url,
                                        use_org_meta: childVideo.use_org_meta,
                                      })}>
                                  <SimpleVideoThumbnail format={childVideo.format}
                                                        imageUrl={childVideo.thumbnail} />
                                </Link>
                                {childVideo.status === VIDEO_STATUS.APPROVED &&
                                  <ApprovalCheckbox titleAccess={'Approved'}/>}
                              </Box>
                            </Grid>
                            <Grid item>
                              <ClipVideoMenu
                                video={childVideo}
                                onEdit={() => setEditVideoRequest(childVideo.uuid)}
                                onApprove={async () => approveVideo(childVideo.uuid, childVideo.status === VIDEO_STATUS.APPROVED ? VIDEO_STATUS.UNAPPROVED : VIDEO_STATUS.APPROVED)}
                                onDelete={async () => await deleteClip(childVideo)}
                                onDownloadMp4={() => download(childVideo.uuid, childVideo.domain)}
                                onDownloadPremierProject={() => getPremierProjectUrl(childVideo.project_id, childVideo.clip_id)}
                                onReUpload={() => setReUploadVideoRequest(childVideo)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <VideoTitle
                                onClick={() => setEditVideoTitle({
                                  uuid: childVideo.uuid,
                                  title: childVideo.title
                                })}>{childVideo.title}</VideoTitle>
                              <Typography fontSize={13}>Score: {childVideo.score}</Typography>
                              <Typography fontSize={13}>Created at: <Moment
                                format="D MMM YYYY">{childVideo.created_at * 1000}</Moment></Typography>
                              {childVideo.media_id &&
                                <div title={`Copy media id to clipboard`} style={{
                                  cursor: 'pointer',
                                  marginTop: '10px'
                                }}>
                                  <SvgIcon
                                    onClick={copyToClipboard(childVideo.media_id)}><CopyContentIcon/></SvgIcon>
                                </div>}
                            </Grid>
                          </Grid>
                        </Grid>);
                    })}
                  </Grid>
                </Grid>
              </Grid>
            </VideoFxRow>);
        })}
      </GridRoot>
    </Box>
    {busy && <LinearProgress color="success"/>}
    <PaginationTable
      defaultSize={20}
      busy={busy}
      startPage={pagination.currentPage - 1}
      count={pagination.totalItems}
      onChange={page =>
        dispatch({
          type: 'SET_PAGINATION',
          payload: {
            currentPage: page + 1,
          },
        })
      }
      updateRowPerPage={totalPerPage => {
        dispatch({
          type: 'SET_PAGINATION',
          payload: {
            currentPage: 1,
            itemsPerPage: totalPerPage,
          },
        });
      }}
    />
  </>;
}